import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Save, FileDown, Plus, X, Target, Users, CalendarDays, MapPin, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const PRESENCE_COLOR = { present: "#10b981", absent: "#ef4444", excuse: "#f59e0b" };
const LEVEL_COLOR = { forte: "#10b981", pertinente: "#0ea5e9", possible: "#94a3b8" };

// Fiche d'activité auto-générée (distincte de l'affiche) : objectifs, habitants retenus,
// objectifs PP concernés, participation réelle, bilan final. Modifiable + export PDF.
export default function ActivityFicheDialog({ open, onClose, entryId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [bilan, setBilan] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!entryId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/pp/activity-fiche/${entryId}`);
      setFiche(data); setObjectives(data.objectives || []); setBilan(data.bilan || "");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); onClose(); }
    setLoading(false);
  }, [entryId, onClose]);

  useEffect(() => { if (open && entryId) load(); }, [open, entryId, load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/pp/activity-fiche/${entryId}`, {
        objectives: objectives.map((o) => o.trim()).filter(Boolean), bilan,
      });
      setFiche(data); setObjectives(data.objectives || []);
      toast.success(t("pp.fiche.saved"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSaving(false);
  };

  const exportPdf = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/pp/activity-fiche/${entryId}/pdf`, "_blank");
  };

  const canEdit = fiche?.can_edit;
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fiche-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-teal-400" />{t("pp.fiche.title")}</DialogTitle>
        </DialogHeader>
        {loading || !fiche ? (
          <div className="py-16 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />…</div>
        ) : (
          <div className="space-y-5">
            {/* En-tête activité */}
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <h3 className="text-lg font-bold text-slate-100">{fiche.activity_name}</h3>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-300">
                <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-teal-400" />{fmtDate(fiche.date)}{fiche.start ? ` · ${fiche.start}${fiche.end ? "–" + fiche.end : ""}` : ""}</span>
                {fiche.place && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-teal-400" />{fiche.place}</span>}
                {fiche.category && <span className="inline-flex items-center gap-1.5"><Tag className="h-4 w-4 text-teal-400" />{fiche.category}</span>}
                {fiche.animators && <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-teal-400" />{fiche.animators}{fiche.function ? ` (${fiche.function})` : ""}</span>}
              </div>
            </div>

            {/* Objectifs de l'activité (éditables) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-teal-300"><Target className="h-4 w-4" />{t("pp.fiche.objectives")}</span>
                {canEdit && <button onClick={() => setObjectives([...objectives, ""])} data-testid="fiche-obj-add" className="inline-flex items-center gap-1 text-xs font-semibold text-teal-300 hover:text-teal-200"><Plus className="h-3.5 w-3.5" />{t("pp.fiche.add")}</button>}
              </div>
              {objectives.length === 0 ? (
                <p className="text-xs text-slate-500 italic">{t("pp.fiche.noObjectives")}</p>
              ) : (
                <div className="space-y-2">
                  {objectives.map((o, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`fiche-obj-${i}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0" />
                      {canEdit ? (
                        <>
                          <Input value={o} onChange={(e) => setObjectives(objectives.map((x, idx) => (idx === i ? e.target.value : x)))} className="h-9 bg-white/[0.04] border-white/10 text-slate-100" data-testid={`fiche-obj-input-${i}`} />
                          <button onClick={() => setObjectives(objectives.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-400 shrink-0"><X className="h-4 w-4" /></button>
                        </>
                      ) : <span className="text-sm text-slate-200">{o}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Habitants & objectifs PP concernés */}
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-teal-300 mb-2"><Users className="h-4 w-4" />{t("pp.fiche.residents")}</span>
              <div className="rounded-xl border border-white/10 overflow-hidden mt-2">
                <table className="w-full text-sm" data-testid="fiche-residents-table">
                  <thead className="text-slate-400 bg-white/[0.03]">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-semibold">{t("pp.fiche.colResident")}</th>
                      <th className="px-3 py-2 font-semibold">{t("pp.fiche.colPpObjectives")}</th>
                      <th className="px-3 py-2 font-semibold">{t("pp.fiche.colPresence")}</th>
                      <th className="px-3 py-2 font-semibold">{t("pp.fiche.colEffect")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fiche.residents.length === 0 ? (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">{t("pp.fiche.noResidents")}</td></tr>
                    ) : fiche.residents.map((r) => (
                      <tr key={r.resident_id} className="border-t border-white/5" data-testid={`fiche-res-${r.resident_id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-100 flex items-center gap-1.5">
                            {r.match_level && <span className="h-2 w-2 rounded-full" style={{ background: LEVEL_COLOR[r.match_level] || "#94a3b8" }} />}
                            {r.full_name}{r.room ? <span className="text-xs text-slate-500">· {r.room}</span> : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-300">
                          {r.pp_objectives.length ? r.pp_objectives.map((o, i) => (
                            <div key={i} className="text-xs">• {o.title}{o.domain ? <span className="text-slate-500"> ({o.domain})</span> : null}</div>
                          )) : <span className="text-slate-600 text-xs">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          {r.presence ? <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: PRESENCE_COLOR[r.presence] }}><span className="h-2 w-2 rounded-full" style={{ background: PRESENCE_COLOR[r.presence] }} />{t(`pp.bilan.${r.presence}`, { defaultValue: r.presence })}</span> : <span className="text-slate-600 text-xs">{t("pp.fiche.notEncoded")}</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-300 text-xs">{r.effect ? t(`pp.bilan.effects.${r.effect}`, { defaultValue: r.effect }) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-2">{t("pp.fiche.participantsCount", { count: fiche.participants_count })} · {t("pp.fiche.retainedCount", { count: fiche.retained_count })}</p>
            </div>

            {/* Bilan final */}
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-teal-300 mb-2">{t("pp.fiche.bilan")}</span>
              {canEdit ? (
                <Textarea value={bilan} onChange={(e) => setBilan(e.target.value)} rows={3} className="mt-1 bg-white/[0.04] border-white/10 text-slate-100" data-testid="fiche-bilan" placeholder={t("pp.fiche.bilanPh")} />
              ) : <p className="text-sm text-slate-300 mt-1 whitespace-pre-line">{bilan || "—"}</p>}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <Button variant="outline" onClick={exportPdf} data-testid="fiche-pdf" className="border-white/15 bg-white/5 hover:bg-white/10 text-slate-100"><FileDown className="h-4 w-4 mr-1.5" />{t("pp.fiche.pdf")}</Button>
              {canEdit && <Button onClick={save} disabled={saving} data-testid="fiche-save" className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />{t("pp.fiche.save")}</>}</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
